import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Input,
  Button,
  Grid,
  Divider,
  Snackbar,
  CssBaseline,
  Select,
  Option,
  FormLabel,
  IconButton,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Textarea,
  Tooltip,
  CircularProgress,
  Chip,
  Breadcrumbs,
} from '@mui/joy';
import {
  Typography as MuiTypography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  KeyboardArrowRight,
} from '@mui/icons-material';
import Link from '@mui/joy/Link';
import { DataGrid } from '@mui/x-data-grid';
import {
  getPermissions,
  getPermission,
  updatePermission,
} from '../../main/API/authentication';
import PermissionGate from '../../main/components/PermissionGate';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';

export default function PermissionsScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [data, setData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState({
    search: '',
    is_active: true,
    app_label: '',
    resource: '',
    action: '',
  });
  const [openModal, setOpenModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    codename: '',
    app_label: '',
    action: '',
    resource: '',
    description: '',
    is_active: true,
  });
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const loadPermissions = useCallback(async () => {
    if (!hasPermission(PERMISSIONS.PERMISSIONS_LIST)) {
      setPermissions([]);
      return;
    }

    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        pagination: true,
        ...filters,
      };
      
      const response = await getPermissions(params);
      if (response && response.data) {
        setData(response.data);
        setPermissions(response.data.results || []);
      } else {
        setPermissions([]);
        setData(null);
      }
    } catch (error) {
      console.error('Failed to load permissions:', error);
      setResponseMessage(error.message || t('settingsPages.permissions.loadFailed'));
      setFailedNotification(true);
      setPermissions([]);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, paginationModel.page, paginationModel.pageSize, filters, t]);

  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize]);

  const handleOpenModal = (permission = null) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({
        name: permission.name || '',
        codename: permission.codename || '',
        app_label: permission.app_label || '',
        action: permission.action || '',
        resource: permission.resource || '',
        description: permission.description || '',
        is_active: permission.is_active !== undefined ? permission.is_active : true,
      });
    } else {
      setEditingPermission(null);
      setFormData({
        name: '',
        codename: '',
        app_label: '',
        action: '',
        resource: '',
        description: '',
        is_active: true,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPermission(null);
  };

  const handleSave = async () => {
    if (!editingPermission) {
      setResponseMessage(t('settingsPages.permissions.createNotAllowed'));
      setFailedNotification(true);
      return;
    }
    if (!hasPermission(PERMISSIONS.PERMISSIONS_UPDATE)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return;
    }
    
    try {
      setLoading(true);
      await updatePermission(editingPermission.id, formData);
      setResponseMessage(t('settingsPages.permissions.updatedSuccess'));
      setSuccessNotification(true);
      handleCloseModal();
      loadPermissions();
    } catch (error) {
      console.error('Failed to save permission:', error);
      setResponseMessage(error.message || t('settingsPages.permissions.saveFailed'));
      setFailedNotification(true);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    loadPermissions();
  };

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const columns = useMemo(() => [
    { field: 'id', headerName: t('settingsPages.permissions.colId'), flex: 1, minWidth: 80 },
    { field: 'name', headerName: t('settingsPages.permissions.colName'), flex: 2.5, minWidth: 200 },
    { 
      field: 'codename', 
      headerName: t('settingsPages.permissions.colCodename'), 
      flex: 2, 
      minWidth: 150,
      renderCell: (params) => (
        <Chip size="small" variant="soft" color="primary">
          {params.row.codename}
        </Chip>
      )
    },
    { field: 'app_label', headerName: t('settingsPages.permissions.colAppLabel'), flex: 1.5, minWidth: 120 },
    { field: 'resource', headerName: t('settingsPages.permissions.colResource'), flex: 1.5, minWidth: 120 },
    { field: 'action', headerName: t('settingsPages.permissions.colAction'), flex: 1.5, minWidth: 120 },
    { 
      field: 'is_active', 
      headerName: t('settingsPages.permissions.colStatus'), 
      flex: 1, 
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          sx={{
            color: params.row.is_active ? 'success.main' : 'neutral.main',
            fontWeight: 500,
          }}
        >
          {params.row.is_active ? t('settingsPages.permissions.statusActive') : t('settingsPages.permissions.statusInactive')}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('settingsPages.permissions.colActions'),
      flex: 2,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {hasPermission(PERMISSIONS.PERMISSIONS_UPDATE) && (
            <Tooltip title={t('settingsPages.permissions.tooltipEdit')}>
              <IconButton
                size="sm"
                variant="soft"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(params.row);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [t, hasPermission, handleOpenModal]);

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width={"100%"}>
            <Box sx={{ mb: 3 }}>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                {[t('settingsPages.permissions.breadcrumbSettings'), t('settingsPages.permissions.breadcrumbCurrent')].map((item) => (
                  <Link key={item} color="neutral" href="/settings">
                    {item}
                  </Link>
                ))}
              </Breadcrumbs>
              <Typography textAlign={"start"} level="h1">{t('settingsPages.permissions.pageTitle')}</Typography>
            </Box>

            <PermissionGate permissions={[PERMISSIONS.PERMISSIONS_LIST]}>
              {/* Filters */}
              <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1 }}>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Input
                    placeholder={t('settingsPages.permissions.searchPlaceholder')}
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    startDecorator={<SearchIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Input
                    placeholder={t('settingsPages.permissions.filterAppLabel')}
                    value={filters.app_label}
                    onChange={(e) => setFilters(prev => ({ ...prev, app_label: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Input
                    placeholder={t('settingsPages.permissions.filterResource')}
                    value={filters.resource}
                    onChange={(e) => setFilters(prev => ({ ...prev, resource: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Input
                    placeholder={t('settingsPages.permissions.filterAction')}
                    value={filters.action}
                    onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Select
                    value={filters.is_active ? 'active' : 'inactive'}
                    onChange={(e, value) => setFilters(prev => ({ ...prev, is_active: value === 'active' }))}
                  >
                    <Option value="active">{t('settingsPages.permissions.filterActive')}</Option>
                    <Option value="inactive">{t('settingsPages.permissions.filterInactive')}</Option>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <Button type="submit" loading={loading} startDecorator={<SearchIcon />}>
                    {t('actions.search')}
                  </Button>
                </Grid>
              </Grid>

              {/* Actions icons */}
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start'
                  }}
                >
                  {/* Create permission button removed - permissions cannot be created */}
                </Grid>
              </Grid>

              {/* Table */}
              <Box>
                <DataGrid
                  rows={permissions || []}
                  columns={columns}
                  loading={loading}
                  pagination
                  paginationModel={paginationModel}
                  paginationMode="server"
                  rowCount={data ? data.count : 0}
                  onPaginationModelChange={(params) => {
                    if (params.pageSize !== paginationModel.pageSize) {
                      handlePageSizeChange(params.pageSize);
                    } else {
                      handlePageChange(params.page);
                    }
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  hideFooterSelectedRowCount
                  getRowHeight={() => 'auto'}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    '& .MuiDataGrid-root': {
                      border: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#f5f5f5',
                      borderBottom: '2px solid #e0e0e0',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#333',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#333',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      lineHeight: '1.5',
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: '#555',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      borderBottom: '1px solid #f0f0f0',
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                      },
                      '&.Mui-selected': {
                        backgroundColor: '#e3f2fd',
                        '&:hover': {
                          backgroundColor: '#bbdefb',
                        },
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '2px solid #e0e0e0',
                      backgroundColor: '#fafafa',
                      fontSize: '14px',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '12px',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '@media (max-width: 600px)': {
                      '& .MuiDataGrid-toolbarContainer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-footer': {
                        flexDirection: 'column',
                        gap: 1,
                      },
                      '& .MuiDataGrid-cell': {
                        minHeight: '60px',
                        alignItems: 'center',
                        padding: '8px',
                        fontSize: '13px',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        fontSize: '13px',
                      },
                    }
                  }}
                />
              </Box>
            </PermissionGate>

            {/* Edit Modal */}
            <Modal open={openModal} onClose={handleCloseModal}>
              <ModalDialog sx={{ maxWidth: 600, width: '100%' }}>
                <ModalClose />
                <Typography level="h4" sx={{ mb: 2 }}>
                  {t('settingsPages.permissions.modalTitle')}
                </Typography>
                <Stack spacing={2}>
                  <FormLabel>{t('settingsPages.permissions.labelName')} *</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('settingsPages.permissions.labelName')}
                  />
                  
                  <FormLabel>{t('settingsPages.permissions.labelCodename')}</FormLabel>
                  <Typography level="body-xs" sx={{ color: 'neutral.600', mb: 0.5 }}>
                    {t('settingsPages.permissions.codenameReadOnly')}
                  </Typography>
                  <Input
                    value={formData.codename}
                    readOnly
                    sx={{ bgcolor: 'background.level1' }}
                    placeholder="permission_codename"
                  />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FormLabel>{t('settingsPages.permissions.labelAppLabel')} *</FormLabel>
                      <Input
                        value={formData.app_label}
                        onChange={(e) => setFormData(prev => ({ ...prev, app_label: e.target.value }))}
                        placeholder="app_label"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormLabel>{t('settingsPages.permissions.labelResource')} *</FormLabel>
                      <Input
                        value={formData.resource}
                        onChange={(e) => setFormData(prev => ({ ...prev, resource: e.target.value }))}
                        placeholder="resource"
                      />
                    </Grid>
                  </Grid>
                  
                  <FormLabel>{t('settingsPages.permissions.labelAction')} *</FormLabel>
                  <Input
                    value={formData.action}
                    onChange={(e) => setFormData(prev => ({ ...prev, action: e.target.value }))}
                    placeholder={t('settingsPages.permissions.codenameHint')}
                  />
                  
                  <FormLabel>{t('settingsPages.permissions.labelDescription')}</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t('settingsPages.permissions.labelDescription')}
                    minRows={3}
                  />
                  
                  <Box>
                    <NativeCheckbox
                      label={t('settingsPages.permissions.labelActive')}
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="outlined" onClick={handleCloseModal}>
                      {t('actions.cancel')}
                    </Button>
                    <PermissionGate permissions={[PERMISSIONS.PERMISSIONS_UPDATE]}>
                      <Button
                        onClick={handleSave}
                        disabled={loading || !editingPermission || !formData.name || !formData.app_label || !formData.resource || !formData.action}
                        startDecorator={loading ? <CircularProgress size="sm" /> : <SaveIcon />}
                      >
                        {loading ? t('settingsPages.permissions.updating') : t('settingsPages.permissions.saveChanges')}
                      </Button>
                    </PermissionGate>
                  </Box>
                </Stack>
              </ModalDialog>
            </Modal>

            {/* Notifications */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={() => setSuccessNotification(false)}
            >
              {responseMessage}
            </Snackbar>

            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={() => setFailedNotification(false)}
            >
              {responseMessage}
            </Snackbar>
          </Box>
        </Box>
      </form>
    </DirectionalMaterialThemeProvider>
  );
}

